/**
 * WHICH ENTITIES A TRANSFER BLOCK CAN OFFER (2026-10-03).
 *
 * Block definitions live in helper files with no reference to the component — the reason
 * AtlasFieldBridge exists. blockly-interface fills this from the entity definitions it already
 * subscribes to; a Transfer block reads it when it is created or loaded.
 *
 * HEADLESS (Drydock) IT IS EMPTY, and that must lose nothing: a block keeps the entities it was saved
 * with in its own mutation, so an empty list only means "nothing new to offer".
 *
 * The server (olympus-functions transfer-rules.ts) filters by platform prefix and status only — never a
 * platform table, never NEW/LOCAL. "Only entities that reference a person" is not a server rule this
 * mirrors: it falls out of the planner, which yields no targets for an entity that does not reference
 * one. An entity offered here that points at no one is accepted by the server and simply moves nothing.
 */
export const PeopleBlockBridge: { referencingEntities: { id: string; name: string }[] } = { referencingEntities: [] };

export interface EntityRow { id: string; name: string; on: boolean }

const PLATFORM_PREFIX = 'systemOotb';
const USER = 'systemOotbUser';

function refersToPeople(fields: any[] | undefined): boolean {
  for (const f of fields || []) {
    if (!f) continue;
    if (f.type === 'ENTITY_REFERENCE' && f.referenceEntityName === USER) return true;
    if (f.type === 'ARRAY_OF_OBJECT' && refersToPeople(f.data)) return true;
  }
  return false;
}

export function entitiesReferencingPeople(defs: any[]): { id: string; name: string }[] {
  return (defs || [])
    .filter((e) => e && typeof e.id === 'string' && !e.id.startsWith(PLATFORM_PREFIX)
      && e.status !== 'NEW' && e.status !== 'LOCAL' && refersToPeople(e.data))
    .map((e) => ({ id: e.id, name: String(e.name || e.id) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * A saved block's rows brought up to date with what the project has now.
 *  - A saved row keeps its tick, under the entity's current name.
 *  - An entity added since starts UNTICKED (owner, 2026-10-03): the builder opts it in.
 *  - With a known list (the editor), a saved entity that no longer points at people is dropped — the
 *    server would refuse the whole transfer over it. With none (headless), every saved row is kept.
 */
export function mergeEntityRows(saved: EntityRow[], current: { id: string; name: string }[]): EntityRow[] {
  if (!current.length) return saved.map((r) => ({ ...r }));
  const byId = new Map(saved.map((r) => [r.id, r] as const));
  return current.map((c) => ({ id: c.id, name: c.name, on: byId.get(c.id)?.on ?? false }));
}
